import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocalizedStrings } from "../../Localization";
import { AdminPanelPage } from "../AdminPanelPage";
import { BreadcrumbProps, Card, CardBody, Input, Button, Select, SelectOptionType } from "@aidkitorg/component-library";
import { usePost } from "../../API";

export const OnSiteIntakePage: React.FC = () => {
  const L = useLocalizedStrings();
  const [dateOfBirth, setDateOfBirth] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [contactType, setContactType] = useState<"phone_number" | "email">("phone_number");
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const strings = L.admin_panel.applicants.on_site_intake;

  // Set up protected search API call
  const searchBeginAPI = usePost("/protected_search", { suppressErrorDisplay: true });

  // Mutation for triggering search
  const searchMutation = useMutation({
    mutationFn: async () => {
      const terms: Record<string, string> = {
        birth_date: dateOfBirth,
      };

      terms[contactType] = contactType === "phone_number" ? phoneNumber : email;
      const fields: ["birth_date", "phone_number" | "email"] = ["birth_date", contactType];

      const result = await searchBeginAPI({ terms, fields });
      return result;
    },
  });

  const breadcrumbs: BreadcrumbProps["items"] = [
    {
      label: L.admin_panel.nav.applicants,
    },
    {
      label: "On-Site Intake",
    },
  ];

  const validatePhoneNumber = (value: string): boolean => {
    if (!value) return true;
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(value);
  };

  const validateEmail = (value: string): boolean => {
    if (!value) return true;
    const emailRegex = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value);
  };

  const handleClearAll = () => {
    setDateOfBirth("");
    setPhoneNumber("");
    setEmail("");
    setContactType("phone_number");
    setPhoneError(false);
    setEmailError(false);
  };

  // Check if form is valid for search
  const isSearchDisabled = !(dateOfBirth && (contactType === "phone_number" ? phoneNumber : email));

  const handleSearch = () => {
    // Only validate the selected contact type
    if (contactType === "phone_number") {
      const hasPhoneError = !validatePhoneNumber(phoneNumber);
      setPhoneError(hasPhoneError);
      setEmailError(false);
      if (hasPhoneError) {
        return;
      }
    } else {
      const hasEmailError = !validateEmail(email);
      setEmailError(hasEmailError);
      setPhoneError(false);
      if (hasEmailError) {
        return;
      }
    }

    // Trigger protected search
    searchMutation.mutate();
  };

  return (
    <AdminPanelPage title={strings.title} description={strings.description} breadcrumbs={breadcrumbs}>
      <div className="flex flex-col w-full">
        <Card className="rounded-md p-2">
          <CardBody>
            <div className="flex flex-col gap-3">
              {/* Header with Filters title and Clear All button */}
              <div className="flex justify-between items-center">
                <div className="text-lg font-semibold m-0">{strings.filters}</div>
                <Button variant="secondary" onClick={handleClearAll}>
                  {strings.clear_all}
                </Button>
              </div>

              {/* Filters */}
              <div className="flex flex-col gap-2">
                {/* First filter: Date of Birth */}
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{strings.where}</span>
                  <span className="font-medium">{strings.date_of_birth}</span>
                  <span className="text-gray-600">{strings.is}</span>
                  <Input
                    label={strings.date_of_birth}
                    showLabel={false}
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    placeholder="MM/DD/YYYY"
                    dateFormat="MM/DD/YYYY"
                  />
                </div>

                <div className="text-sm font-medium text-gray-700 uppercase">{strings.and}</div>

                {/* Contact type selection and input */}
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600 lowercase">{strings.where}</span>
                  <Select
                    label={strings.contact_type}
                    description="Select contact type"
                    hideLabel={true}
                    value={{
                      label: contactType === "phone_number" ? strings.phone_number : strings.email,
                      value: contactType,
                    }}
                    options={[
                      { label: strings.phone_number, value: "phone_number" },
                      { label: strings.email, value: "email" },
                    ]}
                    onChange={(option) => {
                      setContactType(option.value as "phone_number" | "email");
                      setPhoneError(false);
                      setEmailError(false);
                    }}
                    className="w-40"
                  />
                  <span className="text-gray-600">{strings.is}</span>
                  {contactType === "phone_number" ? (
                    <Input
                      label={strings.phone_number}
                      showLabel={false}
                      type="phone"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="(555) 123-4567"
                      formatPattern="(###) ###-####"
                    />
                  ) : (
                    <Input
                      label={strings.email}
                      showLabel={false}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@example.com"
                    />
                  )}
                  {phoneError && <span className="text-red-600">{strings.phone_error}</span>}
                  {emailError && <span className="text-red-600">{strings.email_error}</span>}
                </div>
              </div>

              <div className="flex justify-start mt-2">
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  isDisabled={isSearchDisabled || searchMutation.isPending}
                >
                  {strings.search}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </AdminPanelPage>
  );
};

export default OnSiteIntakePage;
